import { forwardRef, useImperativeHandle, useState, useRef, useEffect } from 'react';
import CustomRadio from '@components/ui/CustomRadio';
import CustomSelect from '@components/ui/CustomSelect';
import InputBoxWithUnit from '@components/ui/InputBoxWithUnit';
import Spinner from '@components/ui/Spinner';
import type { FormProps } from '../../types/form.types';
import { useTheme } from '@theme/useTheme';

export interface Page1Handle {
  validate: () => boolean;
}

const Page1 = forwardRef<Page1Handle, FormProps>(({
  formData,
  updateFormData,
  onPrevious,
  onNext,
  currentPage = 1,
  canGoPrevious = true,
  canGoNext = true,
  isLoading = false,
  error,
}, ref) => {
  const { colors } = useTheme();
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Refs for each field
  const ahiRef = useRef<HTMLDivElement>(null);
  const odiRef = useRef<HTMLDivElement>(null);
  const reiRef = useRef<HTMLDivElement>(null);
  const plmiRef = useRef<HTMLDivElement>(null);
  const meanHeartRateRef = useRef<HTMLDivElement>(null);
  const oxygenSaturationRef = useRef<HTMLDivElement>(null);
  const arousalIndexRef = useRef<HTMLDivElement>(null);
  const respiratoryDisturbanceIndexRef = useRef<HTMLDivElement>(null);
  const sleepStageRef = useRef<HTMLDivElement>(null);
  const sleepPositionRef = useRef<HTMLDivElement>(null);
  const nocturiaRef = useRef<HTMLDivElement>(null);

  // Scroll to first error when errors change
  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      const firstErrorField = Object.keys(errors)[0];
      
      const fieldRefs: Record<string, React.RefObject<HTMLDivElement | null>> = {
        ahi: ahiRef,
        odi: odiRef,
        rei: reiRef,
        plmi: plmiRef,
        meanHeartRate: meanHeartRateRef,
        oxygenSaturation: oxygenSaturationRef,
        arousalIndex: arousalIndexRef,
        respiratoryDisturbanceIndex: respiratoryDisturbanceIndexRef,
        sleepStage: sleepStageRef,
        sleepPosition: sleepPositionRef,
        nocturia: nocturiaRef,
      };

      const refToScroll = fieldRefs[firstErrorField];
      if (refToScroll?.current) {
        refToScroll.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
    }
  }, [errors]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Required field validations
    if (formData.sleepStage === null || formData.sleepStage === undefined || formData.sleepStage === 0) {
      newErrors.sleepStage = 'Sleep stage is required';
    }

    if (formData.sleepPosition === null || formData.sleepPosition === undefined || formData.sleepPosition === 0) {
      newErrors.sleepPosition = 'Sleep position is required';
    }

    if (formData.nocturia === null || formData.nocturia === undefined) {
      newErrors.nocturia = 'Nocturia count is required';
    } else if (formData.nocturia < 0 || formData.nocturia > 10) {
      newErrors.nocturia = 'Nocturia must be between 0 and 10';
    }

    // Optional field validations (validate only if provided)
    if (formData.ahi && formData.ahi.trim() !== '') {
      const ahiValue = parseFloat(formData.ahi);
      if (isNaN(ahiValue)) {
        newErrors.ahi = 'Please enter a valid number';
      } else if (ahiValue < 0 || ahiValue > 100) {
        newErrors.ahi = 'AHI must be between 0 and 100 events/hour';
      }
    }

    if (formData.odi && formData.odi.trim() !== '') {
      const odiValue = parseFloat(formData.odi);
      if (isNaN(odiValue)) {
        newErrors.odi = 'Please enter a valid number';
      } else if (odiValue < 0 || odiValue > 100) {
        newErrors.odi = 'ODI must be between 0 and 100 events/hour';
      }
    }

    if (formData.rei && formData.rei.trim() !== '') {
      const reiValue = parseFloat(formData.rei);
      if (isNaN(reiValue)) {
        newErrors.rei = 'Please enter a valid number';
      } else if (reiValue < 0 || reiValue > 100) {
        newErrors.rei = 'REI must be between 0 and 100 events/hour';
      }
    }

    if (formData.plmi && formData.plmi.trim() !== '') {
      const plmiValue = parseFloat(formData.plmi);
      if (isNaN(plmiValue)) {
        newErrors.plmi = 'Please enter a valid number';
      } else if (plmiValue < 0 || plmiValue > 80) {
        newErrors.plmi = 'PLMI must be between 0 and 80 events/hour';
      }
    }

    if (formData.meanHeartRate && formData.meanHeartRate.trim() !== '') {
      const heartRateValue = parseFloat(formData.meanHeartRate);
      if (isNaN(heartRateValue)) {
        newErrors.meanHeartRate = 'Please enter a valid number';
      } else if (heartRateValue < 40 || heartRateValue > 150) {
        newErrors.meanHeartRate = 'Heart rate must be between 40 and 150 BPM';
      }
    }

    if (formData.oxygenSaturation && formData.oxygenSaturation.trim() !== '') {
      const oxygenValue = parseFloat(formData.oxygenSaturation);
      if (isNaN(oxygenValue)) {
        newErrors.oxygenSaturation = 'Please enter a valid number';
      } else if (oxygenValue < 80 || oxygenValue > 100) {
        newErrors.oxygenSaturation = 'Oxygen saturation must be between 80 and 100%';
      }
    }

    if (formData.arousalIndex && formData.arousalIndex.trim() !== '') {
      const arousalValue = parseFloat(formData.arousalIndex);
      if (isNaN(arousalValue)) {
        newErrors.arousalIndex = 'Please enter a valid number';
      } else if (arousalValue < 0 || arousalValue > 40) {
        newErrors.arousalIndex = 'Arousal index must be between 0 and 40 events/hour';
      }
    }

    if (formData.respiratoryDisturbanceIndex && formData.respiratoryDisturbanceIndex.trim() !== '') {
      const rdiValue = parseFloat(formData.respiratoryDisturbanceIndex);
      if (isNaN(rdiValue)) {
        newErrors.respiratoryDisturbanceIndex = 'Please enter a valid number';
      } else if (rdiValue < 0 || rdiValue > 100) {
        newErrors.respiratoryDisturbanceIndex = 'RDI must be between 0 and 100 events/hour';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  useImperativeHandle(ref, () => ({
    validate,
  }));

  const clearError = (field: string) => {
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  return (
    <div className="w-full max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
      <div
        className="border p-6 sm:p-8 lg:p-10 overflow-hidden"
        style={{
          backgroundColor: colors.background,
          borderColor: colors.border,
          borderTopRightRadius: '2rem',
          borderBottomLeftRadius: '2rem'
        }}
      >
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4 sm:mb-6" style={{ color: colors.heading }}>
          Sleep Study Metrics
        </h2>
        <p className="mb-6 sm:mb-8 text-base sm:text-lg" style={{ color: colors.text }}>
          Fields marked with <span className="font-bold" style={{ color: colors.error }}>*</span> are required
        </p>

        {/* Medical Data Section */}
        <div className="mt-6 sm:mt-8">
          <h3 className="text-lg sm:text-xl font-bold mb-4 sm:mb-6" style={{ color: colors.heading }}>
            Medical Data (Optional)
          </h3>
          <p className="mb-4 sm:mb-6 text-sm sm:text-base" style={{ color: colors.text }}>
            If you have recent sleep study results or medical data, please provide them below. These fields are optional but can help provide a more accurate assessment.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            <div ref={ahiRef}>
              <InputBoxWithUnit
                beforeText="Apnea-Hypopnea Index"
                isSecureText={false}
                value={formData.ahi || ''}
                unitValue="events/hour"
                onChange={(text) => {
                  updateFormData({ ahi: text });
                  clearError('ahi');
                }}
                keyBoardType="numeric"
                hasError={!!errors.ahi}
                errorMessage={errors.ahi}
                placeholder="Enter AHI value"
                min={0}
                max={100}
                description="Number of apneas and hypopneas per hour of sleep (Normal: <5, Mild: 5-14, Moderate: 15-29, Severe: ≥30)"
              />
            </div>

            <div ref={odiRef}>
              <InputBoxWithUnit
                beforeText="Oxygen Desaturation Index"
                isSecureText={false}
                value={formData.odi || ''}
                unitValue="events/hour"
                onChange={(text) => {
                  updateFormData({ odi: text });
                  clearError('odi');
                }}
                keyBoardType="numeric"
                hasError={!!errors.odi}
                errorMessage={errors.odi}
                placeholder="Enter ODI value"
                min={0}
                max={100}
                description="Number of oxygen desaturations per hour of sleep"
              />
            </div>

            <div ref={reiRef}>
              <InputBoxWithUnit
                beforeText="Respiratory Event Index"
                isSecureText={false}
                value={formData.rei || ''}
                unitValue="events/hour"
                onChange={(text) => {
                  updateFormData({ rei: text });
                  clearError('rei');
                }}
                keyBoardType="numeric"
                hasError={!!errors.rei}
                errorMessage={errors.rei}
                placeholder="Enter REI value"
                min={0}
                max={100}
                description="Number of respiratory events per hour of sleep"
              />
            </div>

            <div ref={plmiRef}>
              <InputBoxWithUnit
                beforeText="Periodic Limb Movement Index"
                isSecureText={false}
                value={formData.plmi || ''}
                unitValue="events/hour"
                onChange={(text) => {
                  updateFormData({ plmi: text });
                  clearError('plmi');
                }}
                keyBoardType="numeric"
                hasError={!!errors.plmi}
                errorMessage={errors.plmi}
                placeholder="Enter PLMI value"
                min={0}
                max={80}
                description="Number of limb movements per hour of sleep (Normal: <10, Elevated: >10, Severe: >25)"
              />
            </div>

            <div ref={meanHeartRateRef}>
              <InputBoxWithUnit
                beforeText="Mean Heart Rate"
                isSecureText={false}
                value={formData.meanHeartRate || ''}
                unitValue="BPM"
                onChange={(text) => {
                  updateFormData({ meanHeartRate: text });
                  clearError('meanHeartRate');
                }}
                keyBoardType="numeric"
                hasError={!!errors.meanHeartRate}
                errorMessage={errors.meanHeartRate}
                placeholder="Enter heart rate"
                min={40}
                max={150}
                description="Average heart rate during sleep (Normal resting: 60-100 BPM)"
              />
            </div>

            <div ref={oxygenSaturationRef}>
              <InputBoxWithUnit
                beforeText="Oxygen Saturation"
                isSecureText={false}
                value={formData.oxygenSaturation || ''}
                unitValue="%"
                onChange={(text) => {
                  updateFormData({ oxygenSaturation: text });
                  clearError('oxygenSaturation');
                }}
                keyBoardType="numeric"
                hasError={!!errors.oxygenSaturation}
                errorMessage={errors.oxygenSaturation}
                placeholder="Enter oxygen saturation"
                min={80}
                max={100}
                description="Average oxygen saturation during sleep (Normal: 95-100%, Low: <88%)"
              />
            </div>

            <div ref={arousalIndexRef}>
              <InputBoxWithUnit
                beforeText="Arousal Index"
                isSecureText={false}
                value={formData.arousalIndex || ''}
                unitValue="events/hour"
                onChange={(text) => {
                  updateFormData({ arousalIndex: text });
                  clearError('arousalIndex');
                }}
                keyBoardType="numeric"
                hasError={!!errors.arousalIndex}
                errorMessage={errors.arousalIndex}
                placeholder="Enter arousal index"
                min={0}
                max={40}
                description="Number of arousals per hour of sleep (Normal: <10, Mild: >10, Moderate: >20, Severe: >30)"
              />
            </div>

            <div ref={respiratoryDisturbanceIndexRef}>
              <InputBoxWithUnit
                beforeText="Respiratory Disturbance Index"
                isSecureText={false}
                value={formData.respiratoryDisturbanceIndex || ''}
                unitValue="events/hour"
                onChange={(text) => {
                  updateFormData({ respiratoryDisturbanceIndex: text });
                  clearError('respiratoryDisturbanceIndex');
                }}
                keyBoardType="numeric"
                hasError={!!errors.respiratoryDisturbanceIndex}
                errorMessage={errors.respiratoryDisturbanceIndex}
                placeholder="Enter respiratory disturbance index"
                min={0}
                max={100}
                description="Number of respiratory disturbances per hour (includes AHI + RERAs)"
              />
            </div>
          </div>

          {/* Sleep Stage Analysis */}
          <div className="mt-4 sm:mt-6" ref={sleepStageRef}>
            <label className="block text-sm sm:text-base font-medium mb-2 sm:mb-3" style={{ color: colors.text }}>
              Sleep Stage Analysis <span style={{ color: colors.error }}>*</span>
            </label>
            <p className="text-xs sm:text-sm mb-3" style={{ color: colors.text }}>
              Select the predominant sleep stage based on medical assessment
            </p>
            <div className="space-y-2">
              {[
                { value: 1, label: 'N1 (Light Sleep)' },
                { value: 2, label: 'N2 (Moderate Sleep)' },
                { value: 3, label: 'N3 (Deep Sleep)' },
                { value: 4, label: 'REM Sleep' }
              ].map((option) => (
                <CustomRadio
                  key={option.value}
                  name="sleepStage"
                  description={option.label}
                  checked={formData.sleepStage === option.value}
                  onChange={() => {
                    updateFormData({ sleepStage: option.value });
                    clearError('sleepStage');
                  }}
                />
              ))}
            </div>
            {errors.sleepStage && (
              <p className="mt-1 text-xs" style={{ color: colors.error }}>{errors.sleepStage}</p>
            )}
          </div>

          {/* Sleep Position Picker */}
          <div className="mt-4 sm:mt-6" ref={sleepPositionRef}>
            <CustomSelect
              value={formData.sleepPosition}
              onChange={(value) => {
                updateFormData({ sleepPosition: value === 0 ? null : value });
                clearError('sleepPosition');
              }}
              options={[
                { value: 1, label: 'Supine (Lying on back)' },
                { value: 2, label: 'Non Supine (side or stomach)' },
              ]}
              placeholder="Select Sleep Position"
              hasError={!!errors.sleepPosition}
              errorMessage={errors.sleepPosition}
              required
              label="Sleep Position Preference"
              description="Select your preferred sleeping position"
            />
          </div>

          {/* Nocturia Input */}
          <div className="mt-4 sm:mt-6" ref={nocturiaRef}>
            <InputBoxWithUnit
              beforeText="Nocturia (times per night)"
              isSecureText={false}
              value={formData.nocturia?.toString() || ''}
              unitValue="times/night"
              onChange={(text) => {
                const parsed = parseInt(text, 10);
                updateFormData({ nocturia: isNaN(parsed) ? null : parsed });
                clearError('nocturia');
              }}
              keyBoardType="numeric"
              hasError={!!errors.nocturia}
              errorMessage={errors.nocturia}
              required
              placeholder="Enter number of times"
              min={0}
              max={10}
              description="Number of times you wake up to urinate during sleep (Normal: 0-1, Frequent: ≥2)"
            />
          </div>
        </div>

        {/* Navigation Buttons */}
        {onPrevious && onNext && (
          <div
            className="mt-6 sm:mt-8 pt-6 border-t"
            style={{ borderColor: colors.border }}
          >
            {/* Error Display */}
            {error && (
              <div
                className="mb-4 p-3 rounded-lg border"
                style={{
                  backgroundColor: colors.error + '20',
                  borderColor: colors.error
                }}
              >
                <p className="text-sm" style={{ color: colors.error }}>{error}</p>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
              <button
                onClick={onPrevious}
                disabled={!canGoPrevious || isLoading}
                className={`w-full sm:w-auto px-6 sm:px-8 py-3 font-medium transition-all duration-200 ${canGoPrevious && !isLoading
                  ? 'hover:shadow-lg transform hover:-translate-y-0.5'
                  : 'cursor-not-allowed'
                  }`}
                style={{
                  backgroundColor: canGoPrevious && !isLoading ? colors.secondaryButton : colors.input,
                  color: canGoPrevious && !isLoading ? colors.text : colors.text + '80',
                  borderTopRightRadius: '1rem',
                  borderBottomLeftRadius: '1rem'
                }}
              >
                Previous
              </button>

              {/* Page Indicators - Centered */}
              <div className="flex items-center justify-center space-x-2">
                {[0, 1, 2, 3].map((page) => (
                  <div
                    key={page}
                    className={`w-2 h-2 rounded-full transition-all duration-200 ${page === currentPage
                      ? 'scale-110'
                      : page < currentPage
                        ? 'opacity-60'
                        : 'opacity-30'
                      }`}
                    style={{
                      backgroundColor:
                        page === currentPage
                          ? colors.primary
                          : page < currentPage
                            ? colors.primary
                            : colors.border,
                    }}
                  />
                ))}
              </div>

              <button
                onClick={onNext}
                disabled={!canGoNext || isLoading}
                className={`w-full sm:w-auto px-6 sm:px-8 py-3 font-medium text-white transition-all duration-200 ${canGoNext && !isLoading
                  ? 'hover:shadow-lg transform hover:-translate-y-0.5'
                  : 'cursor-not-allowed'
                  }`}
                style={{
                  backgroundColor: canGoNext && !isLoading ? colors.primaryButton : colors.border,
                  borderTopRightRadius: '1rem',
                  borderBottomLeftRadius: '1rem'
                }}
              >
                {isLoading ? (
                  <Spinner size="sm" color="white" text="Processing..." showText={true} />
                ) : (
                  'Next'
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

Page1.displayName = 'Page1';

export default Page1;
